/**
 * Data-warehouse connectors (#1863): the types the Data connections admin
 * pages share, mirroring the Go views served under
 * /admin/customers/:customer/warehouse. Hand-written, like gotypes.ts: the
 * server is the source of truth for the field schema, so nothing here knows a
 * connector type by name.
 */

/** `generated`: a value Platinum makes when it saves the connection (an external ID, a key pair); the form only shows it. */
export type WarehouseFieldKind = 'text' | 'number' | 'password' | 'json_file' | 'select' | 'generated'

export interface WarehouseFieldOption {
  value: string
  label: string
}

export interface WarehouseFieldShowWhen {
  key: string
  equals: string
}

export interface WarehouseField {
  key: string
  label: string
  kind: WarehouseFieldKind
  required: boolean
  /** The value is the connection's one secret: sent as `secret`, never in `settings`, never read back. */
  secret: boolean
  default?: string | number | null
  options?: WarehouseFieldOption[] | string[] | null
  help?: string
  show_when?: WarehouseFieldShowWhen | null
}

export interface WarehouseConnectorType {
  type: string
  /** Only Platinum makes connections of this type (sqlite, for demos): never offered in the chooser, and not edited on the page. */
  internal?: boolean
  label: string
  blurb: string
  fields: WarehouseField[]
  setup_script: string
  /** A select field whose value picks the script (BigQuery's sign-in); empty when one script serves. */
  setup_script_by?: string
  /** One script per option of setup_script_by. */
  setup_scripts?: Record<string, string>
  /** The chooser's heading: Databases, Warehouses or Query engines. */
  group: string
  /** One sentence on the engine's SQL, for the agent. */
  dialect: string
  /** What the engine calls a namespace: schema, dataset, database. */
  namespace_noun: string
  /** The engine enforces the "Max data shown" limit. */
  byte_cap: boolean
  /** One sentence on the data cap: shown under "Max data shown" when byte_cap, in its place when not (Athena's workgroup cutoff). */
  byte_cap_note?: string
  /** A select in `fields` whose value adds a field set (Amazon RDS's engine); empty when the fields never change. */
  fields_by?: string
  /** The fields each option of fields_by adds. */
  field_sets?: Record<string, WarehouseField[]>
}

export interface WarehouseLimits {
  max_rows: number
  timeout_seconds: number
  max_bytes: number
}

export interface WarehouseSecretState {
  set: boolean
  hint: string
}

/** What a connection view says of its schema snapshot; the tree itself is only for the agent. */
export interface WarehouseSnapshotSummary {
  taken_at: number
  namespace_count: number
  table_count: number
  /** A cap (20 namespaces, 200 tables, 60 columns a table) cut the snapshot. */
  truncated: boolean
}

export interface WarehouseConnection {
  id: string
  name: string
  type: string
  settings: Record<string, unknown>
  secret: WarehouseSecretState
  limits: WarehouseLimits
  last_tested_at: number | null
  /** False until a test passes; read it only when last_tested_at is set. */
  last_test_ok: boolean
  last_test_error: string
  /** What a Manager tells the agent about this connection. */
  agent_notes: string
  /** Null until the first snapshot. */
  schema_snapshot: WarehouseSnapshotSummary | null
  saved_query_count: number
  /** The setup script with this connection's own settings and generated values. */
  setup_script: string
  created_by: string
  created_at: number
  updated_at: number
}

/** The body of create, update and a draft test. `secret` omitted on update keeps the stored one. */
export interface WarehouseConnectionBody {
  /** On a draft test only: the saved connection whose stored secret to use when no new one is sent. */
  id?: string
  name: string
  type: string
  settings: Record<string, unknown>
  secret?: string
  limits?: Partial<WarehouseLimits>
  agent_notes?: string
  /** On update: make the generated values (key pair, external ID) again. */
  regenerate?: boolean
}

/** The longest notes for the agent the server accepts, in characters. */
export const AGENT_NOTES_MAX = 4000

export interface WarehouseTestResult {
  ok: boolean
  error: string
  duration_ms: number
}

export interface WarehouseColumn {
  name: string
  type: string
}

export interface WarehouseQueryResult {
  columns: WarehouseColumn[]
  rows: Record<string, unknown>[]
  row_count: number
  truncated: boolean
  bytes_processed: number
  duration_ms: number
  /** The audit row this run wrote. */
  query_log_id?: string
}

export interface WarehouseSchemaItem {
  name: string
  type?: string
  description?: string
}

export interface WarehouseSchemaResponse {
  level: 'namespaces' | 'tables' | 'columns'
  items: WarehouseSchemaItem[]
}

export type SavedQueryLevel = 'organisation' | 'private'

export interface WarehouseSavedQuery {
  id: string
  connection_id: string
  name: string
  description: string
  sql: string
  columns: WarehouseColumn[]
  level: SavedQueryLevel
  owner_email: string
  last_run_at: number | null
  last_run_rows: number | null
  created_at: number
  updated_at: number
}

export interface WarehouseSavedQueryBody {
  name?: string
  description?: string
  sql?: string
  level?: SavedQueryLevel
}

export interface WarehouseQueryLogEntry {
  id: string
  connection_id: string
  saved_query_id: string | null
  session_id: string | null
  user_email: string
  source: string
  sql: string
  rows: number
  bytes_processed: number
  duration_ms: number
  ok: boolean
  error: string
  started_at: number
}

/** The server has no master key: nothing can be saved or run until PLATINUM_WAREHOUSE_MASTER_KEY is set. */
export const NOT_CONFIGURED_CODE = 'not_configured'

export class WarehouseError extends Error {
  code: string
  constructor(message: string, code = '') {
    super(message)
    this.name = 'WarehouseError'
    this.code = code
  }
}

export const isNotConfiguredError = (error: unknown): boolean =>
  error instanceof WarehouseError && error.code === NOT_CONFIGURED_CODE

const GB = 1024 ** 3

export const bytesToGb = (bytes: number): number => Math.round((bytes / GB) * 1000) / 1000
export const gbToBytes = (gb: number): number => Math.round(gb * GB)

export const formatBytes = (bytes: number): string => {
  if (bytes >= GB) return `${bytesToGb(bytes)} GB`
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

export const formatWarehouseTime = (ms: number | null | undefined): string =>
  ms ? new Date(ms).toLocaleString('en-GB') : 'Never'

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago". */
export const formatAge = (ms: number, now = Date.now()): string => {
  const minutes = Math.max(0, Math.floor((now - ms) / 60000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
  return `${Math.floor(hours / 24)} days ago`
}

/** What the connector type calls a namespace (dataset, schema, database), from the server; "schema" when unknown. */
export const namespaceNoun = (type: Pick<WarehouseConnectorType, 'namespace_noun'> | null | undefined, count: number): string => {
  const noun = type?.namespace_noun || 'schema'
  return count === 1 ? noun : `${noun}s`
}

/** The chooser's headings in order; a group the server adds later follows these. */
export const CONNECTOR_GROUP_ORDER = ['Databases', 'Warehouses', 'Query engines']

/** The types under each group, in CONNECTOR_GROUP_ORDER then first-seen order; types keep the server's order within a group. */
export const groupConnectorTypes = (types: WarehouseConnectorType[]): [string, WarehouseConnectorType[]][] => {
  const groups = new Map<string, WarehouseConnectorType[]>(CONNECTOR_GROUP_ORDER.map((name) => [name, []]))
  for (const t of types) {
    const name = t.group || CONNECTOR_GROUP_ORDER[0]
    groups.set(name, [...(groups.get(name) ?? []), t])
  }
  return [...groups].filter(([, members]) => members.length > 0)
}

/** Field options arrive as {value,label} or bare strings. */
export const fieldOptions = (field: WarehouseField): WarehouseFieldOption[] =>
  (field.options ?? []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o))

export type FieldValues = Record<string, string>

/** A field shows unless its show_when names another field with a different value. */
export const isFieldVisible = (field: WarehouseField, values: FieldValues): boolean =>
  !field.show_when || (values[field.show_when.key] ?? '') === String(field.show_when.equals)

/** The type's fields under the form's values: its own fields, then the set its fields_by select chooses. */
export const fieldsFor = (type: WarehouseConnectorType, values: FieldValues): WarehouseField[] => {
  const set = type.fields_by ? type.field_sets?.[values[type.fields_by] ?? ''] : undefined
  return set ? [...type.fields, ...set] : type.fields
}

const defaultValue = (field: WarehouseField): string => {
  if (field.default !== undefined && field.default !== null) return String(field.default)
  return field.kind === 'select' ? (fieldOptions(field)[0]?.value ?? '') : ''
}

/**
 * The starting values: the connection's settings if it has them, else each
 * field's default. The set the form starts on wins; every other set's fields
 * are seeded too, so switching the engine finds a value for each.
 */
export const initialFieldValues = (connectorType: WarehouseConnectorType, settings?: Record<string, unknown>): FieldValues => {
  const values: FieldValues = {}
  const seed = (fields: WarehouseField[]) => {
    for (const field of fields) {
      if (field.secret || field.key in values) continue
      const stored = settings?.[field.key]
      values[field.key] = stored !== undefined && stored !== null ? String(stored) : field.kind === 'generated' ? '' : defaultValue(field)
    }
  }
  seed(connectorType.fields)
  seed(fieldsFor(connectorType, values))
  for (const set of Object.values(connectorType.field_sets ?? {})) seed(set)
  return values
}

/**
 * The values after the fields_by select changes to `next`: each field of the
 * new set that still holds the old set's default (or nothing) takes the new
 * default (PostgreSQL's port 5432 becomes MySQL's 3306); anything typed stays.
 */
export const switchFieldSet = (type: WarehouseConnectorType, values: FieldValues, next: string): FieldValues => {
  if (!type.fields_by) return values
  const before = fieldsFor(type, values)
  const out = { ...values, [type.fields_by]: next }
  for (const field of fieldsFor(type, out)) {
    if (field.secret || field.kind === 'generated' || field.key === type.fields_by) continue
    const old = before.find((f) => f.key === field.key)
    const current = values[field.key] ?? ''
    if (current === '' || (old && current === defaultValue(old))) out[field.key] = defaultValue(field)
  }
  return out
}

/**
 * The secret field a saved connection's stored secret belongs to: the
 * visible secret field under its own settings. A type with one secret field
 * per sign-in method (Databricks' token or client secret) keeps the stored
 * secret only while that field is the one shown.
 */
export const storedSecretKey = (type: WarehouseConnectorType, settings: Record<string, unknown>): string | undefined => {
  const values = initialFieldValues(type, settings)
  return fieldsFor(type, values).find((f) => f.secret && isFieldVisible(f, values))?.key
}

/** Settings for the body: visible fields only, never a secret or a generated value; numbers as numbers. */
export const buildSettings = (connectorType: WarehouseConnectorType, values: FieldValues): Record<string, unknown> => {
  const settings: Record<string, unknown> = {}
  for (const field of fieldsFor(connectorType, values)) {
    if (field.secret || field.kind === 'generated' || !isFieldVisible(field, values)) continue
    const raw = values[field.key] ?? ''
    if (raw === '') continue
    settings[field.key] = field.kind === 'number' ? Number(raw) : raw
  }
  return settings
}

/** The setup script for the form's current values: the one for the chosen option when the type has several. */
export const setupScriptFor = (type: WarehouseConnectorType, values: FieldValues): string => {
  const by = type.setup_script_by
  return (by && type.setup_scripts?.[values[by]]) || type.setup_script
}

const NUMERIC_TYPE = /^(int|integer|int64|int2|int4|int8|smallint|bigint|float|float4|float8|float64|double|real|numeric|bignumeric|decimal|bigdecimal|number)/i

/**
 * One result cell as text. Numbers in a numeric column (warehouses often send
 * them as strings) get thousands separators from 10,000 up, so a count reads
 * 1,204,311 while a year or a code such as 2026 stays as it is, and show at
 * most 3 decimal places. Display only: saved files keep full precision.
 */
export const formatWarehouseValue = (value: unknown, columnType = ''): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  const n = typeof value === 'number' ? value : NUMERIC_TYPE.test(columnType) && /^-?\d+(\.\d+)?$/.test(String(value)) ? Number(value) : NaN
  if (!Number.isFinite(n)) return String(value)
  if (Math.abs(n) >= 10000) return n.toLocaleString('en-GB', { maximumFractionDigits: 3 })
  return Number.isInteger(n) ? String(value) : String(Number(n.toFixed(3)))
}
